// Official Bokun widget embed. Renders Bokun's hosted booking flow
// (date, pax categories, extras, questions, checkout, payment) using the
// Stripe configured inside Bokun. Reservations sync back to our DB via
// /api/public/bokun/webhook.
//
// Implementation notes:
//  - Channel UUID matches the one used on www.mostrandomexico.com.
//  - The Bokun loader scans for `.bokunWidget` nodes via a MutationObserver
//    after first load. To survive React remounts we (1) only render on the
//    client, (2) keep the widget node stable per productId, and (3) call
//    `BokunWidgetsLoader.reset()` / `init()` whenever the productId changes.
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const CHANNEL_UUID = "e534306c-6722-4b35-a39b-eb33ed8ade97";
const LOADER_SRC = `https://widgets.bokun.io/assets/javascripts/apps/build/BokunWidgetsLoader.js?bookingChannelUUID=${CHANNEL_UUID}`;

interface Props {
  productId: string;
  /** Two-letter ISO code: es | en | pt | it | fr. Defaults to es. */
  lang?: string;
  /** Moneda mostrada en la página (MXN, USD…); Bókun la aplica si el canal la admite. */
  currency?: string;
}

type BokunLoader = {
  init?: () => void;
  reset?: () => void;
  boot?: () => void;
  rescan?: () => void;
};

declare global {
  interface Window {
    BokunWidgetsLoader?: BokunLoader;
    BokunWidgets?: { setCurrency?: (c: string) => void; setLanguage?: (l: string) => void };
  }
}

function ensureLoader(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  const existing = document.querySelector<HTMLScriptElement>("script[data-bokun-loader]");
  if (existing) {
    if (window.BokunWidgetsLoader) return Promise.resolve();
    return new Promise((resolve) => existing.addEventListener("load", () => resolve(), { once: true }));
  }
  return new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = LOADER_SRC;
    s.async = true;
    s.setAttribute("data-bokun-loader", "1");
    s.addEventListener("load", () => resolve(), { once: true });
    document.body.appendChild(s);
  });
}

export function BokunOfficialWidget({ productId, lang, currency, date }: Props & { date?: string | null }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(false);

  // Resolve language: explicit prop > <html lang> > navigator.language > "es".
  const resolvedLang = (() => {
    const allowed = ["es", "en", "pt", "it", "fr", "de"];
    const pick = (v?: string | null) => {
      if (!v) return null;
      const code = v.slice(0, 2).toLowerCase();
      return allowed.includes(code) ? code : null;
    };
    if (typeof window !== "undefined") {
      return (
        pick(lang) ??
        pick(document.documentElement.lang) ??
        pick(navigator.language) ??
        "es"
      );
    }
    return pick(lang) ?? "es";
  })();

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!mounted) return;
    let cancelled = false;
    void ensureLoader().then(() => {
      if (cancelled) return;
      const loader = window.BokunWidgetsLoader;
      try {
        loader?.reset?.();
        loader?.init?.();
        loader?.boot?.();
        loader?.rescan?.();
      } catch {
        /* loader will catch up via its MutationObserver */
      }
    });
    return () => {
      cancelled = true;
    };
  }, [mounted, productId, resolvedLang, currency]);

  // Moneda de la página → calendario oficial. Bókun solo la aplica mediante su
  // API pública `BokunWidgets.setCurrency` (el parámetro de URL se ignora
  // cuando el calendario está incrustado). Reintenta hasta que Bókun cargue.
  useEffect(() => {
    if (!mounted || !currency) return;
    let tries = 0;
    let lastSent = 0;
    const id = window.setInterval(() => {
      tries += 1;
      const api = window.BokunWidgets?.setCurrency;
      if (api && document.querySelector('iframe[src*="experience-calendar"]')) {
        try { api(currency); } catch { /* noop */ }
        lastSent += 1;
        // Se envía varias veces por si el calendario aún no estaba listo.
        if (lastSent >= 3) window.clearInterval(id);
      }
      if (tries > 40) window.clearInterval(id);
    }, 1500);
    return () => window.clearInterval(id);
  }, [mounted, productId, currency]);

  // `experience-calendar` renders only the booking flow (pricing categories,
  // dates, extras, pickup, checkout) without the product hero/description —
  // our page already shows that content in our own design.
  const dataSrc = `https://widgets.bokun.io/online-sales/${CHANNEL_UUID}/experience-calendar/${productId}?lang=${resolvedLang}${currency ? `&currency=${encodeURIComponent(currency)}` : ""}${date ? `&date=${encodeURIComponent(date)}` : ""}`;
  const fallbackHref = dataSrc;

  return (
    <div className="w-full max-w-full overflow-hidden rounded-3xl border border-accent/40 bg-background p-3 sm:p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 px-1">
        <h3 className="text-lg font-bold text-foreground">
          {t("booking.chooseDate", { defaultValue: "Elige tu fecha" })}
        </h3>
        <span className="inline-flex items-center gap-1 rounded-full bg-accent/15 px-2.5 py-1 text-[11px] font-semibold text-foreground">
          <svg aria-hidden viewBox="0 0 24 24" className="h-3.5 w-3.5 text-primary" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z"/></svg>
          {t("booking.secureBooking", { defaultValue: "Reserva segura" })}
        </span>
      </div>

      {mounted ? (
        <div
          key={`${productId}-${resolvedLang}-${currency ?? ""}`}
          ref={ref}
          className="bokunWidget min-h-[520px]"
          data-src={dataSrc}
          data-lang={resolvedLang}
        />
      ) : (
        <div className="min-h-[520px] animate-pulse rounded-xl bg-muted" aria-hidden />
      )}

      <div className="mt-3 flex items-center justify-end text-xs text-muted-foreground">
        <a
          href={fallbackHref}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary hover:underline"
        >
          {t("booking.changeLangCurrency", { defaultValue: "Cambiar idioma/moneda" })}
        </a>
      </div>
    </div>
  );
}
